import React, { createContext, FC, PropsWithChildren, useMemo, useState } from 'react';
import { getLocale } from '../../../util/locales/locales';
import { NavigationAuth, NavigationConfig, NavigationContextType, NavigationState } from '../types/navigation.types';

const initialNavigationState = {
  showBackdrop: false,
  showDesktopSubMenu: null,
  showMobileMenu: false,
  showMobileSubMenu: null,
  showAppSwitcher: false,
};

const initialNavigationConfig = {
  locale: 'en-US',
};

export const NavigationContext = createContext<NavigationContextType>({
  auth: null,
  config: initialNavigationConfig,
  state: initialNavigationState,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setState: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setAuth: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setConfig: () => {},
});

export const NavigationContextProvider: FC<PropsWithChildren<{ config?: NavigationConfig; auth?: NavigationAuth }>> = ({
  children,
  config = {},
  auth,
}) => {
  const [navigationState, setNavigationState] = useState<NavigationState>(initialNavigationState);
  const [navigationConfig, setNavigationConfig] = useState<NavigationConfig>(() => {
    const returnValue = { ...initialNavigationConfig, ...config };

    if (!config.locale) {
      const localeFromUrl = getLocale()?.RFC5646;
      if (localeFromUrl) {
        return { ...returnValue, locale: localeFromUrl };
      }
    }
    return returnValue;
  });

  const [navigationAuth, setNavigationAuth] = useState<NavigationAuth | undefined>(auth);

  const contextValue = useMemo(
    () => ({
      state: navigationState,
      config: navigationConfig,
      auth: navigationAuth,
      setAuth: setNavigationAuth,
      setState: setNavigationState,
      setConfig: setNavigationConfig,
    }),
    [navigationState, navigationConfig, navigationAuth]
  );

  return <NavigationContext.Provider value={contextValue}>{children}</NavigationContext.Provider>;
};

export const useNavigation = () => {
  const ctx = React.useContext(NavigationContext);

  if (!ctx) {
    throw new Error('useNavigation must be used within the NavigationContextProvider');
  }

  const { state, setState, config, setConfig, auth, setAuth } = ctx;
  return { state, setState, config, setConfig, auth, setAuth };
};

export const useNavigationState = () => {
  const ctx = React.useContext(NavigationContext);

  if (!ctx) {
    throw new Error('useNavigationState must be used within the NavigationContextProvider');
  }

  const { state, setState } = ctx;
  return { state, setState };
};
